import React, { useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { Button, Contact, CountrySelectField, H1, Icon, Input, InternationalContact, StatusBadge, colors, toast, useGraphQLMutation, useGraphQLQuery } from '@tviklax/ui-components';
import { DashboardContainer } from '../../features/dashboard/components/styles';
import { useCountries } from '../../features/create-order/components/addressData';
import { useRole } from '../../context/RoleContext';

const PAGE_SIZE = 50;

const MY_ACCOUNTS_QUERY = gql`
  query MyAccounts($accountType: String!, $search: String, $limit: Int, $offset: Int) {
    myAccounts(accountType: $accountType, search: $search, limit: $limit, offset: $offset) {
      totalCount
      items {
        id
        name
        email
        phone
        country
        contactCount
        isRegistered
        relationshipStatus
        addedAt
        onboardingStatus
        invitedAt
      }
    }
  }
`;

const MY_ASSIGNED_ACCOUNTS_QUERY = gql`
  query MyAssignedAccounts($accountType: String!) {
    myAssignedAccounts(accountType: $accountType) {
      id
      name
      email
    }
  }
`;

const ACCOUNT_CONTACTS_QUERY = gql`
  query AccountContacts($accountType: String!, $accountId: Int!) {
    accountContacts(accountType: $accountType, accountId: $accountId) {
      id
      name
      role
      team
      email
      phone
    }
  }
`;

const ADD_TRADE_PARTNER_ACCOUNT_MUTATION = gql`
  mutation AddTradePartnerAccount($input: AddTradePartnerAccountInput!) {
    addTradePartnerAccount(input: $input) {
      success
      message
    }
  }
`;

const INVITE_TRADE_PARTNER_ACCOUNT_MUTATION = gql`
  mutation InviteTradePartnerAccount($vendorId: Int!) {
    inviteTradePartnerAccount(vendorId: $vendorId) {
      success
      message
    }
  }
`;

type AccountsTab = 'vendor' | 'customer';

type OnboardingStatus = 'not_invited' | 'invited' | 'onboarded';

interface Account {
  id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
  country?: string | null;
  contactCount: number;
  isRegistered: boolean;
  relationshipStatus: string;
  addedAt?: string | null;
  onboardingStatus: OnboardingStatus;
  invitedAt?: string | null;
}

interface AssignedAccount {
  id: number;
  name: string;
  email?: string | null;
}

interface AccountContact {
  id: number;
  name: string;
  role?: string | null;
  team?: string | null;
  email?: string | null;
  phone?: string | null;
}

const initials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const formatDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const tabLabel = (tab: AccountsTab) => (tab === 'vendor' ? 'Suppliers' : 'Customers');

const ONBOARDING_BADGE: Record<OnboardingStatus, { variant: 'neutral' | 'pending' | 'success'; label: string }> = {
  not_invited: { variant: 'neutral', label: 'Not yet on Tvikla' },
  invited: { variant: 'pending', label: 'Invited — KYC pending' },
  onboarded: { variant: 'success', label: 'Onboarded' },
};

export const AccountsPage: React.FC = () => {
  const { role } = useRole();
  const isAgent = role === 'agent';
  const defaultTab: AccountsTab = role === 'supplier' ? 'customer' : 'vendor';
  const countries = useCountries();

  const [activeTab, setActiveTab] = useState<AccountsTab>(defaultTab);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [ownerAccountId, setOwnerAccountId] = useState<number | null>(null);
  const [formError, setFormError] = useState('');
  const [expandedAccountId, setExpandedAccountId] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const switchTab = (tab: AccountsTab) => {
    setActiveTab(tab);
    setSearchInput('');
    setDebouncedSearch('');
    setShowForm(false);
    setExpandedAccountId(null);
    setFormError('');
  };

  const { data, loading, refetch } = useGraphQLQuery<{ myAccounts: { items: Account[]; totalCount: number } }>(
    MY_ACCOUNTS_QUERY,
    {
      showErrorToast: false,
      variables: { accountType: activeTab, search: debouncedSearch || null, limit: PAGE_SIZE, offset: 0 },
    }
  );

  const { data: assignedData } = useGraphQLQuery<{ myAssignedAccounts: AssignedAccount[] }>(
    MY_ASSIGNED_ACCOUNTS_QUERY,
    { showErrorToast: false, variables: { accountType: activeTab }, skip: !isAgent }
  );
  const assignedAccounts = assignedData?.myAssignedAccounts ?? [];

  const { data: contactsData, loading: contactsLoading } = useGraphQLQuery<{ accountContacts: AccountContact[] }>(
    ACCOUNT_CONTACTS_QUERY,
    {
      showErrorToast: false,
      variables: { accountType: activeTab, accountId: expandedAccountId },
      skip: expandedAccountId === null,
    }
  );

  const [addAccount, { loading: saving }] = useGraphQLMutation(ADD_TRADE_PARTNER_ACCOUNT_MUTATION, {
    showSuccessToast: false,
    showErrorToast: false,
  });

  const [inviteAccount, { loading: inviting }] = useGraphQLMutation(INVITE_TRADE_PARTNER_ACCOUNT_MUTATION, {
    showSuccessToast: false,
    showErrorToast: false,
  });
  const [invitingId, setInvitingId] = useState<number | null>(null);

  const handleInvite = async (vendorId: number) => {
    setInvitingId(vendorId);
    try {
      const response = await inviteAccount({ variables: { vendorId } });
      const result = response.data?.inviteTradePartnerAccount;
      toast({
        title: result?.success ? 'Invite recorded' : "Couldn't invite",
        description: result?.message,
        variant: result?.success ? undefined : 'destructive',
      });
      if (result?.success) refetch();
    } catch (err) {
      toast({
        title: "Couldn't invite",
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setInvitingId(null);
    }
  };

  const accounts = data?.myAccounts.items ?? [];

  // If the agent has exactly one assigned account on this side, use it
  // automatically — no reason to make them pick from a list of one.
  useEffect(() => {
    setOwnerAccountId(isAgent && assignedAccounts.length === 1 ? assignedAccounts[0].id : null);
  }, [isAgent, assignedAccounts]);

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setCountry('');
    setFormError('');
  };

  const handleSubmit = async () => {
    setFormError('');
    if (!name.trim() || !email.trim()) {
      setFormError('Name and email are required.');
      return;
    }
    if (isAgent && ownerAccountId == null) {
      setFormError('Choose which of your accounts this partner belongs to.');
      return;
    }

    const input: Record<string, unknown> = {
      accountType: activeTab,
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      country: country.trim() || undefined,
    };
    if (isAgent) {
      if (activeTab === 'vendor') input.ownerCustomerId = ownerAccountId;
      else input.ownerVendorId = ownerAccountId;
    }

    try {
      const response = await addAccount({ variables: { input } });
      const result = response.data?.addTradePartnerAccount;
      if (!result?.success) {
        setFormError(result?.message || 'Could not add account.');
        return;
      }
      setShowForm(false);
      resetForm();
      refetch();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not add account.');
    }
  };

  return (
    <DashboardContainer>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <H1 style={{ margin: 0 }}>Accounts</H1>
            <div style={{ fontSize: 14, color: colors.gray500 }}>
              Your known {tabLabel(activeTab).toLowerCase()} — the companies you engage with, independent of any specific order.
            </div>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowForm((v) => !v);
            }}
          >
            {showForm ? 'Cancel' : `+ Add ${activeTab === 'vendor' ? 'supplier' : 'customer'}`}
          </Button>
        </div>

        {isAgent && (
          <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${colors.gray200}` }}>
            {(['vendor', 'customer'] as AccountsTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => switchTab(tab)}
                style={{
                  padding: '10px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab ? `2px solid ${colors.primary}` : '2px solid transparent',
                  color: activeTab === tab ? colors.primary : colors.gray500,
                  cursor: 'pointer',
                }}
              >
                My {tabLabel(tab)}
              </button>
            ))}
          </div>
        )}

        {showForm && (
          <div
            style={{
              background: '#fff',
              border: `1px solid ${colors.gray200}`,
              borderRadius: 14,
              padding: 22,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label="Company name" value={name} onChange={(e) => setName(e.target.value)} />
              <Contact variant="email" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <InternationalContact
                variant="cell"
                label="Phone (optional)"
                labelStyle="default"
                value={phone}
                onChange={setPhone}
                defaultCountry={country || 'IN'}
              />
              <CountrySelectField
                label="Country (optional)"
                value={country}
                onChange={setCountry}
                items={countries}
              />
            </div>
            {isAgent && assignedAccounts.length > 1 && (
              <select
                value={ownerAccountId ?? ''}
                onChange={(e) => setOwnerAccountId(e.target.value ? Number(e.target.value) : null)}
                style={{ padding: '10px 14px', border: `1px solid ${colors.gray200}`, borderRadius: 8, fontSize: 14 }}
              >
                <option value="">Add on behalf of…</option>
                {assignedAccounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name}
                  </option>
                ))}
              </select>
            )}
            {formError && <div style={{ fontSize: 13, color: colors.error }}>{formError}</div>}
            <div>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving ? 'Adding…' : `Add ${activeTab === 'vendor' ? 'supplier' : 'customer'}`}
              </Button>
            </div>
          </div>
        )}

        <Input placeholder="Search by name or email…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />

        <div style={{ background: '#fff', border: `1px solid ${colors.gray200}`, borderRadius: 14, overflow: 'hidden' }}>
          {loading && !data && <div style={{ padding: 22, color: colors.gray500 }}>Loading accounts…</div>}
          {!loading && accounts.length === 0 && (
            <div style={{ padding: 22, color: colors.gray500 }}>
              {debouncedSearch
                ? `No ${tabLabel(activeTab).toLowerCase()} match "${debouncedSearch}".`
                : `No ${tabLabel(activeTab).toLowerCase()} yet — add one, or they'll appear automatically the first time you create an order together.`}
            </div>
          )}
          {accounts.map((account, index) => {
            const isExpanded = expandedAccountId === account.id;
            return (
              <div key={account.id}>
                <div
                  onClick={() => setExpandedAccountId(isExpanded ? null : account.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: '18px 22px',
                    borderTop: index === 0 ? 'none' : `1px solid ${colors.gray100}`,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: colors.gray100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 700,
                      color: colors.gray600,
                      flexShrink: 0,
                    }}
                  >
                    {initials(account.name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: 15 }}>{account.name}</span>
                      {activeTab === 'vendor' ? (
                        <StatusBadge
                          size="sm"
                          variant={ONBOARDING_BADGE[account.onboardingStatus].variant}
                          label={ONBOARDING_BADGE[account.onboardingStatus].label}
                        />
                      ) : (
                        !account.isRegistered && (
                          <StatusBadge size="sm" variant="neutral" label="Not yet on Tvikla" />
                        )
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: colors.gray500 }}>
                      {[account.email, account.phone, account.country].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: colors.gray400, textAlign: 'right', flexShrink: 0 }}>
                    <div>
                      {account.contactCount} contact{account.contactCount === 1 ? '' : 's'}
                    </div>
                    {account.addedAt && <div>Since {formatDate(account.addedAt)}</div>}
                  </div>
                  {activeTab === 'vendor' && account.onboardingStatus !== 'onboarded' && (
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInvite(account.id);
                      }}
                      disabled={inviting && invitingId === account.id}
                    >
                      {inviting && invitingId === account.id
                        ? 'Inviting…'
                        : account.onboardingStatus === 'invited'
                          ? 'Re-invite'
                          : 'Invite'}
                    </Button>
                  )}
                  <Icon
                    name="ChevronDown"
                    size={16}
                    style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms', flexShrink: 0 }}
                  />
                </div>
                {isExpanded && (
                  <div style={{ padding: '0 22px 18px 78px', background: colors.light }}>
                    {contactsLoading && <div style={{ fontSize: 13, color: colors.gray500 }}>Loading contacts…</div>}
                    {!contactsLoading && (contactsData?.accountContacts ?? []).length === 0 && (
                      <div style={{ fontSize: 13, color: colors.gray500, paddingTop: 12 }}>
                        No visible contacts at this account yet.
                      </div>
                    )}
                    {(contactsData?.accountContacts ?? []).map((contact) => (
                      <div key={contact.id} style={{ padding: '8px 0', fontSize: 13, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600 }}>{contact.name}</span>
                        {contact.role && <span style={{ color: colors.gray500 }}>{contact.role}</span>}
                        {contact.email && <span style={{ color: colors.gray500 }}>{contact.email}</span>}
                        {contact.phone && <span style={{ color: colors.gray500 }}>{contact.phone}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </DashboardContainer>
  );
};

export default AccountsPage;
